import { FC, useRef } from 'react'
import { transparentize } from 'polished'
import { IconButtonOnInvertedBackground } from './iconButton'
import styled from 'styled-components'
import useTranslation from 'next-translate/useTranslation'
import { ID } from 'graphql-ws'
import { CartAddIconDefault } from '@/components/atoms/icons/CartAddIconDefault'
import LoaderIcon from '@/components/atoms/svg/LoaderIcon'
import { Flex } from '@/components/atoms/Grid'

type AddToCartButtonProps = {
  onClick: (e: Event) => Promise<void>
  productId?: ID
  title?: string
  addImmediately?: boolean
  hasLoaderIcon?: boolean
} & Rest

const AddToCartButton: FC<AddToCartButtonProps> = ({
  productId,
  title,
  addImmediately = false,
  hasLoaderIcon = true,
  onClick,
  ...rest
}) => {
  const { t } = useTranslation('common')
  const ref = useRef<HTMLDivElement>()
  return (
    <StyledBuyButton
      ref={ref}
      IconElement={<CartAddIconDefault />}
      AfterIconElement={
        <Flex pl={11} className="loader">
          {hasLoaderIcon && <LoaderIcon />}
        </Flex>
      }
      title={title ?? t('Products.Cart.add')}
      onClick={(e: Event) => {
        const disabled =
          ref.current?.classList?.contains &&
          ref.current.classList.contains('disabled')
        if (addImmediately && ref.current?.classList?.add) {
          ref.current.classList.add('disabled')
        }
        if (!disabled) {
          setTimeout(async () => {
            await onClick(e)
            if (addImmediately && ref.current?.classList?.remove) {
              setTimeout(() => {
                if (ref.current?.classList?.remove) {
                  ref.current.classList.remove('disabled')
                }
              }, 200)
            }
          }, 1)
        }
      }}
      data-product_id={productId}
      {...rest}
    />
  )
}

export default AddToCartButton

const StyledBuyButton = styled(IconButtonOnInvertedBackground)`
  background-color: ${(props) => props.theme.colors.accentSecondary};
  color: ${(props) => props.theme.colors.background} !important;
  border-radius: ${(props) => props.theme.buttonBorderRadius}px;
  cursor: pointer;
  &:hover {
    background-color: ${(props) =>
      transparentize(0.03, props.theme.colors.accentSecondary)};
  }
  & .loader {
    visibility: hidden;
  }
  &.disabled {
    opacity: 0.6;
  }
  &.disabled .loader {
    visibility: visible;
  }
  &.disabled:hover {
    cursor: default;
  }
`
